import { useEffect, useRef, useState } from 'react';

import {
  type CalendarDateTime,
  getLocalTimeZone,
  now,
  toCalendarDateTime,
  today,
  toZoned,
} from '@internationalized/date';
import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';

import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import {
  type AssetsCreatePublicationJobSchema,
  type AssetsPublicationDraftSchemaNullable,
  type AssetsPublicationTemplateSchemaWithIdNullable,
  httpGetPublicationsDraftsByAssetId,
  httpGetPublicationsToken,
  httpPostPublicationsJobs,
} from '@iconik-io/iconik-api/src/services/assets';
import { httpGetByObjectTypeByObjectId } from '@iconik-io/iconik-api/src/services/metadata';
import { SelectTemplateField } from '@iconik-io/shared-lib/src/react/components/shared/ui/form_fields/select_template_field/select_template_field';
import { useEntitySimple } from '@iconik-io/shared-lib/src/react/hooks/entity/use_entity_simple';
import { Button, DatetimeCalendarPopover, Icon } from '@iconik-io/uad';

import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { infoMessage, warningMessage } from '../../ducks/messages/thunks';
import { selectDateTimeProperties } from '../../ducks/settings/select_datetime_properties';
import { getCurrentSystemDomainId } from '../../ducks/system_domains/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import { selectMediaFormats } from '../asset_files/selectors';
import {
  convertMetadataValuesToWildmokaFormat,
  type MetadataByObjectTypeResponse,
} from './convert_metadata_values_to_wildmoka_format';
import {
  type ThumbnailMenuOption,
  type ThumbnailSelection,
  ThumbnailsSelector,
} from './thumbnails_selector/thumbnails_selector';
import { WildmokaIframeApi } from './wildmoka_iframe_api/wildmoka_iframe_api';

import styles from './asset_publish.module.css';

type PendingAction = 'publish' | 'schedule' | 'draft';

function PublishPanel() {
  const { id: assetId, versionId } = useParams({ strict: false });
  const dispatch = useAppDispatch();

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  const [iframeSrc, setIframeSrc] = useState<string>();

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [selectedThumbnail, setSelectedThumbnail] = useState<
    ThumbnailSelection | undefined
  >();
  const [selectedThumbnailOption, setSelectedThumbnailOption] = useState<
    ThumbnailMenuOption | undefined
  >();
  const [draftThumbnail, setDraftThumbnail] =
    useState<AssetsPublicationDraftSchemaNullable['thumbnail']>();
  const [isDraftLoading, setIsDraftLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeApiRef = useRef<WildmokaIframeApi>(new WildmokaIframeApi());
  const mediaFormats = useAppSelector((state) =>
    selectMediaFormats(state, assetId, versionId),
  );
  const { entity: asset } = useEntitySimple('assets', assetId);

  const { hourCycle: dateTimeHourCycle } = useAppSelector(
    selectDateTimeProperties,
  );

  const hasWmFormat = mediaFormats.some((format) => format.name === 'WILDMOKA');
  const handleIframeLoad = () => {
    setIsIframeLoaded(true);
  };
  const systemDomainId = useAppSelector(getCurrentSystemDomainId);
  const clipId = asset?.clip_id ?? 0;
  const isDraftAvailable = clipId !== 0;

  useEffect(() => {
    if (!iframeRef.current) {
      return;
    }

    iframeApiRef.current.init(iframeRef.current);
  }, []);

  useEffect(() => {
    if (!assetId) {
      return;
    }

    dispatch(httpGetPublicationsToken())
      .then((response) => {
        if (
          !response.body.api_base_url ||
          !response.body.ui_base_url ||
          !response.body.token
        ) {
          setIsIframeLoaded(false);
          return;
        }

        iframeApiRef.current.setTargetOrigin(`${response.body.ui_base_url}`);

        const redirectParams = new URLSearchParams({ asset_id: assetId });
        if (systemDomainId) {
          redirectParams.set('csi_ext_id', systemDomainId);
        }

        const loginParams = new URLSearchParams({
          jwt: response.body.token,
          // cspell:ignore backoffice
          redirect: `/backoffice/publish_panel/?${redirectParams}`,
        });

        // cspell:ignore eapis
        const src = `${response.body.api_base_url}/eapis/2.0/ui/login?${loginParams}`;
        setIframeSrc(src);
      })
      .catch(() => {
        setIsIframeLoaded(false);
      });
  }, [assetId, systemDomainId]);

  useEffect(() => {
    if (!assetId) {
      setIsDraftLoading(false);
      return;
    }

    let isOutdated = false;

    dispatch(httpGetPublicationsDraftsByAssetId(assetId))
      .then((response) => {
        if (isOutdated) {
          return;
        }

        setSelectedTemplateId(response.body.template?.id ?? null);
        setDraftThumbnail(response.body.thumbnail);
      })
      .finally(() => {
        if (!isOutdated) {
          setIsDraftLoading(false);
        }
      });

    return () => {
      isOutdated = true;
    };
  }, [assetId]);

  const fetchMetadataValues = async () => {
    if (!assetId) {
      return undefined;
    }
    const response = await dispatch(
      httpGetByObjectTypeByObjectId('assets', assetId),
    ).catch((error) => {
      if (!(error instanceof EndpointError)) {
        throw error;
      }
      if (error.response.status !== 404) {
        throw error;
      }
      return undefined;
    });

    if (!response) {
      return undefined;
    }

    // The endpoint's response type has no index signature, so the field-keyed
    // shape this reads is not expressible against it.
    // biome-ignore lint/plugin: see above.
    const metadataValueEntries = response.body as MetadataByObjectTypeResponse;

    if (!metadataValueEntries) {
      return undefined;
    }

    const metadataValues =
      convertMetadataValuesToWildmokaFormat(metadataValueEntries);

    return metadataValues;
  };

  const handleTemplateChange = async (
    templateId: AssetsPublicationTemplateSchemaWithIdNullable['id'] | null,
  ) => {
    setSelectedTemplateId(templateId);

    if (templateId === null) {
      iframeApiRef.current.setTemplateId(null, null);
      return;
    }

    const publishMetadataValues = await fetchMetadataValues();
    iframeApiRef.current.setTemplateId(
      templateId,
      publishMetadataValues ?? null,
    );
  };

  const submitPublicationJob = async (
    action: PendingAction,
    publishAtTime?: string,
  ) => {
    if (!iframeRef.current) {
      return;
    }

    setPendingAction(action);

    try {
      const formData = await iframeApiRef.current.getPublishFormData();
      const {
        social_accounts,
        invalid_fields,
        all_fields_valid,
        ...publishData
      } = formData;
      if (all_fields_valid === false || invalid_fields?.length) {
        dispatch(
          warningMessage({
            text: 'Please review the form and correct the highlighted fields before publishing.',
          }),
        );
        return;
      }

      if (social_accounts?.length === 0) {
        dispatch(
          warningMessage({
            text: 'Please select at least one destination before publishing.',
          }),
        );
        return;
      }

      if (!assetId) {
        return;
      }
      const data: AssetsCreatePublicationJobSchema = {
        ...publishData,
        asset_id: assetId,
      };

      if (publishAtTime) {
        data.publish_at_time = publishAtTime;
      }

      if (action === 'draft') {
        data.save_clip_as_draft = true;
      }

      if (selectedTemplateId) {
        data.template = {
          id: selectedTemplateId,
          apply: {
            apply_title: false,
            apply_viewports_on_components: true,
          },
        };
      }

      if (
        selectedThumbnail?.type === 'KEYFRAME' &&
        'asset_id' in selectedThumbnail.keyframe &&
        selectedThumbnail.keyframe.asset_id
      ) {
        data.thumbnail = {
          type: selectedThumbnail.type,
          asset_id: selectedThumbnail.keyframe.asset_id,
          keyframe_id: selectedThumbnail.keyframe.id,
        };
      }

      if (selectedThumbnail?.type === 'KEYFRAME_EXTERNAL_ASSET') {
        data.thumbnail = {
          type: 'KEYFRAME',
          asset_id: selectedThumbnail.assetId,
          keyframe_id: selectedThumbnail.keyframe.id,
        };
      }

      await dispatch(httpPostPublicationsJobs(data));
      dispatch(
        segmentTrackEvent({
          eventName: 'Published to Wildmoka',
          properties: {
            WithTemplate: Boolean(selectedTemplateId),
            Thumbnail: selectedThumbnailOption ?? 'none',
            Scheduled: Boolean(publishAtTime),
            Draft: action === 'draft',
            VideoSource: Boolean(data.video_source),
          },
        }),
      );

      if (isDraftAvailable && data.save_clip_as_draft) {
        dispatch(infoMessage({ text: 'Draft updated' }));
      }

      if (!data.video_source && !hasWmFormat) {
        let postType = 'Publication job created';
        if (data.save_clip_as_draft) {
          postType = 'Saving draft';
        }
        if (publishAtTime) {
          postType = 'Schedule job created';
        }

        if (data.save_clip_as_draft && isDraftAvailable) {
          return;
        }
        dispatch(
          infoMessage({
            text: `${postType}. Processing may take longer as this asset needs to be transcoded into a publishable Wildmoka format first.`,
          }),
        );
      }
    } finally {
      setPendingAction(null);
    }
  };

  const handleScheduleConfirm = (scheduledAt: CalendarDateTime) => {
    const publishAtTime = toZoned(
      scheduledAt,
      getLocalTimeZone(),
    ).toAbsoluteString();

    return submitPublicationJob('schedule', publishAtTime);
  };

  if (!assetId) {
    return null;
  }

  return (
    <>
      {!isIframeLoaded ? <LoadingBar /> : null}
      <div
        className={classNames(styles.asset_publish, {
          [styles.loaded]: isIframeLoaded,
        })}
      >
        <div className={styles.content}>
          <div className={styles.top_controls}>
            <SelectTemplateField
              onSelectionChange={handleTemplateChange}
              selectedTemplateId={selectedTemplateId}
              ariaLabel="Template"
              name="publish_panel_template_id"
              isClearable
              placeholder="Select publish template"
            />
            <ThumbnailsSelector
              assetId={assetId}
              versionId={versionId}
              thumbnail={selectedThumbnail}
              draftThumbnail={draftThumbnail}
              isDraftLoading={isDraftLoading}
              onChangeThumbnail={(thumbnailSelection) => {
                setSelectedThumbnail(thumbnailSelection);
              }}
              onChangeSelectedOption={setSelectedThumbnailOption}
            />
          </div>
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            className={styles.iframe}
            title="Publish Panel"
            onLoad={handleIframeLoad}
          />
        </div>
        <div className={styles.footer}>
          <div>
            <Button
              variant="tertiary"
              onPress={() => submitPublicationJob('draft')}
              isDisabled={pendingAction !== null}
              isPending={pendingAction === 'draft'}
              pendingLabel={
                isDraftAvailable ? 'Updating draft' : 'Saving draft'
              }
            >
              {isDraftAvailable ? 'Update draft' : 'Save draft'}
            </Button>
          </div>
          <div className={styles.footer_actions}>
            <DatetimeCalendarPopover
              applyLabel="Schedule"
              minValue={now(getLocalTimeZone())}
              maxValue={today(getLocalTimeZone()).add({ years: 3 })}
              defaultValue={toCalendarDateTime(today(getLocalTimeZone()))}
              onChange={handleScheduleConfirm}
              placement="top end"
              hourCycle={dateTimeHourCycle}
            >
              <Button
                variant="tertiary"
                icon={<Icon name="calendar_datetime" size="xs" />}
                isDisabled={pendingAction !== null}
                isPending={pendingAction === 'schedule'}
                pendingLabel="Scheduling"
              >
                Schedule
              </Button>
            </DatetimeCalendarPopover>
            <Button
              variant="primary"
              onPress={() => submitPublicationJob('publish')}
              isDisabled={pendingAction !== null}
              isPending={pendingAction === 'publish'}
              pendingLabel="Publishing"
            >
              Publish
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default PublishPanel;
